import { type TUseGetSocialApplyByStatusResponse, useBatchPatchSocialApply } from "@api";
import type { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import type { ObjectId } from "@bbj-types/utils";
import { useGlobalModalConfirmation } from "@complex/GlobalModalConfirmation";
import ArchiveIcon from "@mui/icons-material/ArchiveOutlined";
import { Divider, IconButton, Stack, Typography } from "@mui/material";
import { ListVirtualizedV2 } from "@styleguide";
import { useState } from "react";
import { RefusedCandidatesCard } from "./card";
import { RefusedCandidatesHeader } from "./header";

interface IRefusedCandidatesProps {
  data: TUseGetSocialApplyByStatusResponse<SocialApplyStatus.REFUSED_STRUCTURE>["data"];
  loading?: boolean;
  refetchApplies: () => void;
}

const RefusedCandidatesTitle = ({
  data,
  loading,
  socialApplySelected,
  handleArchive,
}: {
  data: unknown[];
  loading?: boolean;
  socialApplySelected: ObjectId[];
  handleArchive: (ids: ObjectId[]) => void;
}) => {
  return (
    <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 2, minHeight: 60 }}>
      <Stack>
        {loading ? (
          <Typography
            variant="subtitle1SemiBold"
            sx={{
              color: "primary.main",
            }}
          >
            Candidats refusés
          </Typography>
        ) : (
          <Typography
            variant="subtitle1SemiBold"
            sx={{
              color: "primary.main",
            }}
          >
            {data.length} candidat{data.length <= 1 ? "" : "s"} refusé{data.length <= 1 ? "" : "s"}
          </Typography>
        )}
      </Stack>
      {socialApplySelected.length > 0 && (
        <Stack sx={{ flexDirection: "row", gap: 2 }}>
          <Divider orientation="vertical" flexItem sx={{ borderWidth: "1px" }} />
          <Stack sx={{ alignItems: "center" }}>
            <IconButton color="primary" onClick={() => handleArchive(socialApplySelected)}>
              <ArchiveIcon />
            </IconButton>
            <Typography variant="body2" color="primary">
              Archiver les dossiers
            </Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export const RefusedCandidates = ({ data, loading = false, refetchApplies }: IRefusedCandidatesProps) => {
  const confirm = useGlobalModalConfirmation();
  const [socialApplySelected, setSocialApplySelected] = useState<ObjectId[]>([]);
  const [, archiveSocialApply] = useBatchPatchSocialApply();

  const handleArchive = async (ids: ObjectId[]) => {
    if (
      await confirm(
        <Typography variant="h4">Archiver les dossiers</Typography>,
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography>
            Vous êtes sur le point d'archiver les dossiers des candidats sélectionnés. Ils n'apparaîtront plus dans la
            page de l'offre.
          </Typography>
          <Typography>C'est votre dernier mot ?</Typography>
        </Stack>,
        { confirmLabel: "Valider" }
      )
    ) {
      const res = await archiveSocialApply({
        ids,
      });
      if (res.status === 204) {
        setSocialApplySelected([]);
        refetchApplies();
      }
    }
  };
  return (
    <Stack sx={{ height: 900, overflowX: "hidden", overflowY: "auto", flex: "auto", pl: 8, pr: 8, gap: 2 }}>
      <RefusedCandidatesTitle
        data={data}
        loading={loading}
        socialApplySelected={socialApplySelected}
        handleArchive={handleArchive}
      />
      <ListVirtualizedV2
        data={data || []}
        itemContent={(_, candidate) => (
          <RefusedCandidatesCard
            data={candidate}
            socialApplySelected={socialApplySelected}
            setSocialApplySelected={setSocialApplySelected}
          />
        )}
        components={{
          Header: () => (
            <RefusedCandidatesHeader
              allSocialApply={data.map((item) => item._id)}
              socialApplySelected={socialApplySelected}
              setSocialApplySelected={setSocialApplySelected}
            />
          ),
        }}
      />
    </Stack>
  );
};
